import { useCallback, useEffect, useRef, useState } from "react";

// useChannelDigitBuffer — classic TV behaviour: viewer types one or more
// digits, the buffer commits to a channel either when:
//   • inactivity timeout elapses (1.2s after the last keystroke),
//   • the buffer is at the longest possible channel-number length
//     (no longer-prefix match exists in the channels list → no ambiguity,
//     no reason to wait), or
//   • the viewer presses Enter / SELECT explicitly.
//
// The commit callback (`onCommit(num)`) is responsible for actually
// tuning the CRT — this hook only manages buffer state + timing. The
// buffer is exposed as a string so the UI can render "CH 12_" while
// the user is still typing. `clear()` lets the UI reset on a fail.
//
// Wired into both the keyboard handler (App.js) and the remote digit
// pad (Remote.js); they share the same buffer instance so a "1" typed
// on the keyboard concatenates with a "2" tapped on the remote, just
// like multitap on a real TV remote.

const COMMIT_DELAY_MS = 1200;

export function useChannelDigitBuffer({ availableNumbers, onCommit, enabled = true }) {
  const [buffer, setBuffer] = useState("");
  const timerRef = useRef(null);
  const bufferRef = useRef("");
  const numbersRef = useRef(new Set());
  const onCommitRef = useRef(onCommit);

  useEffect(() => { onCommitRef.current = onCommit; }, [onCommit]);
  useEffect(() => {
    // Pre-compute the set of valid channel numbers once per `availableNumbers`
    // change so the hot-path (every digit press) does O(1) membership tests
    // instead of re-scanning the array. Numbers stored as strings to align
    // with the buffer's string representation.
    numbersRef.current = new Set((availableNumbers || []).map(String));
  }, [availableNumbers]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearTimer();
    bufferRef.current = "";
    setBuffer("");
  }, [clearTimer]);

  const commit = useCallback(() => {
    const raw = bufferRef.current;
    if (!raw) return;
    const num = parseInt(raw, 10);
    reset();
    if (!Number.isFinite(num)) return;
    if (onCommitRef.current) onCommitRef.current(num);
  }, [reset]);

  // Returns true if any valid channel number starts with `prefix` *and*
  // has at least one more digit — i.e. there's a longer match the viewer
  // might still be typing. Used to decide whether to commit immediately
  // or wait for more keystrokes.
  const hasLongerMatch = useCallback((prefix) => {
    if (!prefix) return false;
    for (const n of numbersRef.current) {
      if (n.length > prefix.length && n.startsWith(prefix)) return true;
    }
    return false;
  }, []);

  const pushDigit = useCallback((digit) => {
    if (!enabled) return;
    const d = String(digit);
    if (!/^[0-9]$/.test(d)) return;
    // Cap buffer length at 3 — no real channel-number scheme would go beyond
    // that for a 90s-style retro TV. Prevents pathological hammering.
    const next = (bufferRef.current + d).slice(-3);
    bufferRef.current = next;
    setBuffer(next);
    clearTimer();

    // Immediate-commit path: if no longer-prefix channel exists, the viewer
    // can't possibly want a deeper number — fire the tune right away so it
    // feels snappy. Same trick real TVs use ("3" on a 1-12 cabinet tunes
    // immediately because no "3x" channel exists).
    if (!hasLongerMatch(next)) {
      commit();
      return;
    }

    // Otherwise wait for the inactivity window. Resetting on every keystroke
    // gives the viewer the expected "keep typing, I'll catch you up" feel.
    timerRef.current = setTimeout(commit, COMMIT_DELAY_MS);
  }, [enabled, clearTimer, commit, hasLongerMatch]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return { buffer, pushDigit, commit, reset };
}
